import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiDateFilterBar, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface ReferralCase {
  id: number
  file_number: number | null
  case_number: string | null
  case_name: string | null
  date_added: string | null
  investigation_type: string | null
  other_type: string | null
  referred_by: string | null
  referred_by_customer_id: number | null
  referred_by_contact_id: number | null
  case_total: number
  status: string
}

interface ReferralCustomer {
  customer_id: number
  customer_name: string | null
  referral_count: number
  total_billable: number
}

interface Props {
  mode: "detail" | "summary"
  cases?: ReferralCase[]
  customers?: ReferralCustomer[]
  customer: { id: number; name: string } | null
  contact: { id: number; name: string; customer_id: number | null } | null
  start_date: string
  end_date: string
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return "-"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 25

const caseColumns: HpiColumn<ReferralCase>[] = [
  {
    header: "ID",
    width: "max-w-[60px]",
    align: "right",
    render: (row) => (
      <Link
        href={`/cases/${row.id}`}
        className="text-sm text-blue-600 hover:underline"
      >
        {row.file_number ?? row.id}
      </Link>
    ),
  },
  {
    header: "Date",
    width: "max-w-[100px]",
    render: (row) => (
      <Link
        href={`/cases/${row.id}`}
        className="text-sm text-blue-600 hover:underline"
      >
        {formatDate(row.date_added)}
      </Link>
    ),
  },
  {
    header: "Type",
    render: (row) => (
      <div className="text-sm">
        {row.investigation_type}
        {row.other_type && (
          <div className="text-muted-foreground text-xs">{row.other_type}</div>
        )}
      </div>
    ),
  },
  {
    header: "Case",
    render: (row) => (
      <Link
        href={`/cases/${row.id}`}
        className="text-sm text-blue-600 hover:underline"
      >
        {row.case_name}
      </Link>
    ),
  },
  {
    header: "Referred By",
    render: (row) => <div className="text-sm">{row.referred_by ?? "-"}</div>,
  },
  {
    header: "Est. Billable",
    width: "max-w-[120px]",
    align: "right",
    render: (row) => (
      <div className="text-sm">
        {row.case_total > 0 ? formatCurrency(row.case_total) : ""}
      </div>
    ),
  },
  {
    header: "Status",
    width: "max-w-[120px]",
    align: "right",
    render: (row) => <div className="text-sm">{row.status}</div>,
  },
]

const customerColumns = (
  startDate: string,
  endDate: string,
): HpiColumn<ReferralCustomer>[] => [
  {
    header: "Customer",
    render: (row) => (
      <Link
        href={`/reports/referrals?customer_id=${row.customer_id}&start_date=${startDate}&end_date=${endDate}`}
        className="text-sm font-medium text-blue-600 hover:underline"
      >
        {row.customer_name ?? "-"}
      </Link>
    ),
  },
  {
    header: "#",
    width: "max-w-[60px]",
    align: "right",
    render: (row) => <div className="text-sm">{row.referral_count}</div>,
  },
  {
    header: "Total",
    width: "max-w-[140px]",
    align: "right",
    render: (row) => (
      <div className="text-sm">{formatCurrency(row.total_billable)}</div>
    ),
  },
]

/* ── Main Component ────────────────────────────────────── */

export default function ReferralsReport({
  mode,
  cases = [],
  customers = [],
  customer,
  contact,
  start_date,
  end_date,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [page, setPage] = useState(1)

  const data = mode === "detail" ? cases : customers
  const totalPages = Math.ceil(data.length / PER_PAGE)
  const paginated = data.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const title = contact
    ? `Referred Cases — ${contact.name}`
    : customer
      ? `Referred Cases — ${customer.name}`
      : "Referrals by Customer"

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Reports", href: "/reports/referrals_by_contact" },
    { title: "Referrals by Contact", href: "/reports/referrals_by_contact" },
    { title: contact?.name ?? customer?.name ?? "Referrals", href: "#" },
  ]

  const handleUpdate = () => {
    setPage(1)
    const queryParams: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (customer) queryParams.customer_id = String(customer.id)
    if (contact) queryParams.contact_id = String(contact.id)

    router.get("/reports/referrals", queryParams, { preserveState: true })
  }

  // Summary footer for detail mode
  const caseCount = cases.length
  const caseTotal = cases.reduce((sum, c) => sum + c.case_total, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={title} />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        {mode === "detail" ? (
          <>
            <HpiDataTable<ReferralCase>
              title={title}
              columns={caseColumns}
              data={paginated as ReferralCase[]}
              keyFn={(row) => row.id}
              emptyMessage="No referred cases found for the selected date range."
              pagination={
                cases.length > PER_PAGE ? (
                  <HpiPagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={cases.length}
                    perPage={PER_PAGE}
                    onPageChange={setPage}
                  />
                ) : undefined
              }
            />
            {cases.length > 0 && (
              <div className="mt-2 flex justify-end gap-8 text-sm font-semibold">
                <span>{caseCount} Cases</span>
                <span>{formatCurrency(caseTotal)}</span>
              </div>
            )}
          </>
        ) : (
          <HpiDataTable<ReferralCustomer>
            title="Referrals by Customer"
            columns={customerColumns(startDate, endDate)}
            data={paginated as ReferralCustomer[]}
            keyFn={(row) => row.customer_id}
            emptyMessage="No referral customers found for the selected date range."
            pagination={
              customers.length > PER_PAGE ? (
                <HpiPagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={customers.length}
                  perPage={PER_PAGE}
                  onPageChange={setPage}
                />
              ) : undefined
            }
          />
        )}
      </div>
    </AppLayout>
  )
}
